import { ChangeDetectionStrategy, Component, forwardRef, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { Badge } from '../badge/badge';
import { Icon } from '../icon/icon';
import type { IconName } from '../icon/icon-name';
import { BaseControl } from './base-control';

export interface Segment {
  value: string;
  label: string;
  icon?: IconName;
  /** Red count badge next to the label (e.g. pending items). */
  badge?: number;
}

/**
 * Segmented control (design system SegmentedControl, figma 297:3532).
 * ControlValueAccessor over the selected segment's string value; behaves as a
 * radiogroup. The active segment is the raised action-fill "key", inactive
 * segments sit flat on the sunken group.
 *
 *   <app-segmented-control [(ngModel)]="view" [segments]="[
 *     { value: 'grid', label: t('grid') }, { value: 'map', label: t('map') },
 *   ]" />
 */
@Component({
  selector: 'app-segmented-control',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Badge, Icon],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SegmentedControl), multi: true },
  ],
  template: `
    <div class="group" role="radiogroup" [class.lg]="size() === 'lg'">
      @for (seg of segments(); track seg.value) {
        <button
          type="button"
          role="radio"
          class="segment"
          [class.on]="seg.value === value()"
          [attr.aria-checked]="seg.value === value()"
          [disabled]="disabled()"
          (click)="select(seg.value)"
          (blur)="onTouched()"
        >
          @if (seg.badge != null) {
            <app-badge color="red" [count]="seg.badge" />
          }
          @if (seg.icon; as icon) {
            <app-icon class="seg-icon" [name]="icon" [size]="24" />
          }
          <span class="seg-label">{{ seg.label }}</span>
        </button>
      }
    </div>
  `,
  styles: `
    .group {
      display: inline-flex;
      align-items: stretch;
      width: fit-content;
      border-radius: var(--radius-control);
      overflow: hidden;
      background: var(--surface-3);
      box-shadow: var(--shadow-hairline);
    }

    .segment {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      min-height: 32px;
      padding: 0 8px;
      border: none;
      background: transparent;
      border-radius: var(--radius-control);
      font:
        var(--font-weight-regular) var(--text-body-1) / 24px
        var(--font-body);
      color: var(--ink-2);
      white-space: nowrap;
    }
    .group.lg .segment {
      min-height: 48px;
    }

    .segment:hover:not(:disabled):not(.on) {
      background: var(--surface-sunken);
      color: var(--ink-1);
      box-shadow: inset 0 0 0 0.5px var(--core-overlay-200);
    }

    /* The active segment is a raised action-fill "key" clipped by the group. */
    .segment.on {
      border-radius: 0;
      background: var(--action-fill);
      color: var(--action-text);
      font-weight: var(--font-weight-semibold);
      box-shadow:
        inset 0 0 0 0.5px var(--overlay-white-200),
        0 0 0 0.5px var(--overlay-white-200),
        0 0 2px 0 var(--core-overlay-200),
        0 2px 4px 0 var(--core-overlay-200);
    }

    .segment:disabled {
      opacity: 0.6;
    }

    .segment:focus-visible {
      outline: 2px solid var(--focus-ring);
      outline-offset: -2px;
    }
  `,
})
export class SegmentedControl extends BaseControl<string> {
  readonly segments = input.required<Segment[]>();
  readonly size = input<'default' | 'lg'>('default');

  protected select(value: string): void {
    if (value === this.value()) return;
    this.setValue(value);
  }
}
